// @flow
import {
  getBasePathModularUI,
  getSetting,
  getUploadPath,
  HTTP_METHODS,
} from "../constants";

import xhr from "../utils/fetch/xhr";

import type {
  FilesizeConstraintsType,
  FiletypeConstraintsType,
} from "../models";
import ErrorModel from "../models/error/ErrorModel";
import { ErrorResponse } from "../models";

type ProgressHandler = (file: File, uploadInfo: Object) => void;

type UploadRequestOptions = {
  origin?: string,
  contextPath?: string,
};

type UploadResponse = {
  token: string,
};

/**
 * Upload a file to the upload file service of Be Informed
 */
class UploadRequest {
  _uploadConstraints: {
    fileTypes: FiletypeConstraintsType,
    maxFileSize: FilesizeConstraintsType,
    acceptedFiles: Array<string>,
  };
  _progressHandler: ProgressHandler;
  _origin: string = "";
  _contextPath: string = getBasePathModularUI();

  constructor(
    uploadConstraints: {
      fileTypes: FiletypeConstraintsType,
      maxFileSize: FilesizeConstraintsType,
      acceptedFiles: Array<string>,
    },
    progressHandler: ProgressHandler,
    options?: UploadRequestOptions,
  ) {
    this._uploadConstraints = uploadConstraints;
    this._progressHandler = progressHandler;
    this._origin = options?.origin || "";
    this._contextPath = options?.contextPath || getBasePathModularUI();
  }

  /**
   */
  getFileExtension(file: File): string {
    return file.name.split(".").pop().toLowerCase();
  }

  /**
   */
  isNotAllowedFileType(file: File): boolean {
    const allowedFileTypes = this._uploadConstraints.fileTypes;

    if (allowedFileTypes.length > 0) {
      const fileExtension = this.getFileExtension(file);

      return !allowedFileTypes.some((fileType) =>
        fileType.extensions.includes(fileExtension),
      );
    }

    return false;
  }

  /**
   */
  exceedsMaxFileSize(file: File): boolean {
    const maxFileSize = this._uploadConstraints?.maxFileSize?.fileSize ?? -1;
    return maxFileSize > -1 && file.size > maxFileSize;
  }

  /**
   */
  uploadFile(file: File): Promise<UploadResponse> {
    const maxFileSize = this._uploadConstraints?.maxFileSize?.fileSize ?? -1;

    if (
      getSetting("USE_CLIENTSIDE_VALIDATION") &&
      this.exceedsMaxFileSize(file)
    ) {
      this._progressHandler(file, { error: "errorExceedsMaxFileSize" });
      return Promise.reject(
        new ErrorModel(
          "Constraint.File.MaxFileSizeExceeded",
          "Maximum file upload size is ${max-filesize}",
          {
            "max-filesize": maxFileSize,
          },
          true,
        ),
      );
    }

    if (
      getSetting("USE_CLIENTSIDE_VALIDATION") &&
      this.isNotAllowedFileType(file)
    ) {
      this._progressHandler(file, { error: "errorExtensionNotAllowed" });
      return Promise.reject(
        new ErrorModel(
          "Constraint.File.InvalidExtension",
          "Allowed extensions are: ${extensions}",
          {
            extension: this.getFileExtension(file),
            extensions: this._uploadConstraints.fileTypes.join(", "),
          },
          true,
        ),
      );
    }

    return xhr({
      url: getUploadPath(this._contextPath, this._origin),
      method: HTTP_METHODS.POST,
      headers: {
        "Content-Type": file.type,
        "x-filename": encodeURIComponent(file.name),
        "x-filesize": file.size.toString(),
      },
      onProgress: (e: ProgressEvent) => {
        if (this._progressHandler && e.lengthComputable) {
          this._progressHandler(file, {
            progress: Math.ceil((e.loaded / e.total) * 100),
          });
        }
      },
      data: file,
    })
      .then((response) => {
        this._progressHandler(file, { progress: 100, token: response.token });
        return response;
      })
      .catch((e) => {
        throw new ErrorResponse(e);
      });
  }
}

export default UploadRequest;
